import type { ValueStatus } from './types';

const STATUS_COPY: Record<ValueStatus, string> = {
  observed: 'From reported activity data',
  modeled: 'Estimated with ClimatePlans methods',
  interpolated: 'Estimated for years without a direct observation',
  extrapolated: 'Estimated for years without a direct observation',
  missing: 'Data not available',
  not_modeled: 'Not included in the ClimatePlans model',
  unresolved_breakout: 'Not available as a separate observation from the current calculation',
  not_applicable: 'Not applicable',
  below_threshold: 'Below reporting threshold',
};

export function memberStatusLabel(status: ValueStatus | string | undefined): string {
  if (!status) return 'Data not available';
  return STATUS_COPY[status as ValueStatus] ?? 'Data not available';
}

export const CITY_ONLY_COPY =
  'This community inventory report is available for incorporated cities and towns. County and statewide views are not included in this version.';

export const EMPTY_INVENTORY_COPY =
  'A community inventory could not be assembled for this place and year.';

export const PROTOCOL_COPY =
  'This report uses the ClimatePlans community inventory method. It has not been independently certified under ICLEI or the U.S. Community Protocol.';

export const ACTIVITY_UNAVAILABLE_COPY =
  'Activity and emission-factor values are shown only when they were already produced by the current ClimatePlans calculation. Missing activity is not filled with zero. Heavy-duty miles are labeled contextual when they are not the identity that produced the displayed HDV emissions.';

export const REPLICA_COMPARISON_COPY =
  'ClimatePlans uses modeled light-duty travel activity from Replica. This is estimated travel demand, not observed traffic counts, and it is not compared here to independent highway-performance counts.';

export const TWO_LENS_COPY =
  'The community inventory and the household consumption inventory use different boundaries. They must not be added, subtracted, or combined as a ratio.';

export const HISTORICAL_WARNING =
  'This is a descriptive ClimatePlans software series, not a certified protocol-comparable inventory time series.';

export const PROVISIONAL_FOOTER =
  'Provisional ClimatePlans estimate. Internal preview — not a published municipal inventory.';

export const CALCULATION_COVERAGE_COPY =
  'The current ClimatePlans calculation returned estimates for the sectors shown. On the GHG inventory page, landfill waste and wastewater are combined as Waste & wastewater. Wastewater is also shown as a separate section on the Waste + page. Underlying activity-data completeness has not been independently verified for this report.';

export const UNUSUAL_DENOMINATOR_COPY =
  'Resident population is an unusual denominator for this inventory, so per-resident emissions are not shown.';

export const CBEI_MODEL_VERSION_COPY =
  'The underlying consumption model version is not currently included with the report data.';

export const LIVE_MEMBER_LIMITATIONS = [
  'Values come from the current ClimatePlans calculation.',
  'On the GHG inventory page, landfill waste and wastewater are combined as Waste & wastewater. Wastewater is also shown separately on the Waste + page, year-matched or scaled from the stored inventory year.',
  'Historical results are descriptive and have not been validated as a protocol-consistent time series.',
  'Consumption estimates use a modeled household count and lack an attached model-version identifier.',
] as const;

export const LIVE_TECHNICAL_NOTES = [
  'The report maps the same fetchGHGInventoryData result used on the GHG page; it does not recalculate sector formulas.',
  'Wastewater is overlaid from the stored ClimatePlans wastewater inventory. When that inventory year differs, it is scaled by service population (residents plus workplace workers), or by resident population if workers are unavailable for the inventory year. The GHG inventory page adds that wastewater estimate to landfill waste as Waste & wastewater.',
  'A finite or zero sector total does not establish that the underlying activity was observed.',
  'Agriculture is omitted from charts and the member table when the calculation returns a placeholder zero.',
  'Electricity provider identity is shown as applicable for the inventory year and is not a year-stamped observation.',
  'Where the GHG page fills missing inputs as zero, this report follows that same calculation.',
  'CBEI community totals are scaled from stored grams per household using the modeled household count (hhcount).',
] as const;

/** Member headline: never asserts verified completeness from finite live values. */
export function qualityHeadline(): string {
  return CALCULATION_COVERAGE_COPY;
}

export function communityContextParagraph(input: {
  name: string;
  year: number;
  population: number | null;
  density: number | null;
  utility: string | null | undefined;
}): string {
  const { name, year, population, density, utility } = input;
  const parts: string[] = [];
  if (population != null) {
    let sentence = `${name} had ${population.toLocaleString('en-US')} residents in ${year}`;
    if (density != null) {
      sentence += `, with a population density of ${Math.round(density).toLocaleString('en-US')} residents per square mile`;
    }
    sentence += '.';
    parts.push(sentence);
  } else {
    parts.push(`${name} is shown for inventory year ${year}.`);
  }
  if (typeof utility === 'string' && utility.trim()) {
    parts.push(`${utility} is listed as the electricity provider applicable to the inventory year.`);
  }
  return parts.join(' ');
}

export function findingLabelForSector(sectorCode: string | undefined, sectorLabel: string): string {
  if (sectorCode === 'ldv' || /^light-duty vehicles?$/i.test(sectorLabel)) return 'Light-duty vehicle travel';
  if (sectorCode === 'hdv' || /^heavy-duty vehicles?$/i.test(sectorLabel)) return 'Heavy-duty vehicle travel';
  return sectorLabel;
}

export function reportDocumentTitle(jurisdictionName: string, year: number): string {
  return `${jurisdictionName} Community Greenhouse Gas Inventory ${year} | ClimatePlans`;
}
